import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  ModalBody,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { EPollType } from '@/client/legacy-shared-modules/poll/schema';
import { useUpdateCacheOnPostCreation } from '@/components/PostingExperience/useUpdateCacheOnPostCreation';
import { PollForm } from '@/components/post-form/PollForm';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { useAiTagging } from '@/hooks/useAiTagging';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { analyzeContent, post_created } from '@/analytics';
import { AnnouncePostModal } from '@/imports/ui/components/feed-item/AnnouncePostModal';

import {
  CreatePollModal_createPollV2,
  CreatePollModal_createPollV2Variables,
} from './graphql-types/CreatePollModal_createPollV2';

import { PostingModalFooter } from '../PostingModalFooter';

const TAGS_FOR_AI = gql`
  query CreatePollModal_TagsForAi($input: MultiTagInput) {
    tagsWithSearchTerm(input: $input) {
      tags {
        _id
        name
      }
    }
  }
`;

const FRAGMENT = gql`
  fragment CreatePollModal_PollV2 on PollV2 {
    _id
    group {
      _id
      isGlobalSingleton
    }
  }
`;

const CREATE_POLL = gql`
  mutation CreatePollModal_createPollV2($data: CreatePollInput_V2!) {
    createPollV2(input: $data) {
      data {
        ...CreatePollModal_PollV2
        ...UseUpdateCacheOnPostCreation_PostV2Fragment
        author {
          _id
          userOnboardingChecklistState {
            userHasPosted
          }
        }
      }
    }
  }
  ${FRAGMENT}
  ${useUpdateCacheOnPostCreation.postFragment}
`;

type ICreatePollModalProps = {
  onClose: () => void;
  group?: { _id: string };
  organization?: { _id: string };
  defaultIsSponsoredPost?: boolean;
  tags?: { _id: string; name: string }[];
};

export type PollFormValues = {
  title: string;
  group: string;
  options: { label: string }[];
  body: EditorState;
  isSponsoredPost: boolean;
  isAnnouncement: boolean;
  tags?: { _id: string; name: string }[];
  scheduledPublishDate: Date | null;
};

export const CreatePollModal: React.FC<ICreatePollModalProps> = (props) => {
  const [isAnnounceModalOpen, setIsAnnounceModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const toast = useToast();

  const methods = useForm<PollFormValues>({
    defaultValues: {
      title: '',
      group: props.group?._id,
      options: [{ label: '' }, { label: '' }],
      body: EditorState.createEmpty(),
      isSponsoredPost: !!props.defaultIsSponsoredPost,
      isAnnouncement: false,
      tags: (props.tags || []).filter((t) => !!t?._id && !!t?.name),
      scheduledPublishDate: null,
    },
  });
  const { handleSubmit, formState, watch, setValue, getValues } = methods;

  const isAnnouncement = watch('isAnnouncement');
  const scheduledPublishDate = watch('scheduledPublishDate');
  const title = watch('title');
  const body = watch('body');

  const { data: tagsData } = useQuery(TAGS_FOR_AI, {
    variables: {
      input: { limit: 500, offset: 0, filter: { searchText: '' } },
    },
  });
  const aiLabels = tagsData?.tagsWithSearchTerm?.tags ?? [];

  const { recommendedHashtags } = useAiTagging({
    title,
    bodyPlainText: body?.getCurrentContent?.()?.getPlainText?.() ?? '',
    labels: aiLabels,
    enabled: true,
    onResult: React.useCallback(
      ({ selectedHashtags }) => {
        const current = (getValues('tags') ?? []).filter(
          (t: { _id?: string; name?: string }) => !!t?._id && !!t?.name
        );
        const currentIds = new Set(current.map((t: { _id: string }) => t._id));
        const fromAi = selectedHashtags
          .filter((t) => !!t?._id && !!t?.name)
          .filter((t) => !currentIds.has(t._id));
        if (fromAi.length > 0) {
          setValue('tags', [...current, ...fromAi], {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      },
      [getValues, setValue]
    ),
  });

  const [createPoll] = useMutation<
    CreatePollModal_createPollV2,
    CreatePollModal_createPollV2Variables
  >(CREATE_POLL, { refetchQueries: ['AccountMenu_Query'] });

  const { updateCacheOnPostCreation } = useUpdateCacheOnPostCreation();

  const onSubmit = async (params: PollFormValues) => {
    setLoading(true);
    try {
      const result = await createPoll({
        variables: {
          data: {
            isAnnouncement: params.isAnnouncement,
            data: {
              title: params.title,
              body: convertToRaw(params.body.getCurrentContent()),
              scheduledPublishDate: params.scheduledPublishDate?.toISOString(),
              type: EPollType.MULTIPLE_CHOICE,
              tagIds: (params.tags ?? [])
                .map((tag) => tag?._id)
                .filter((id): id is string => !!id),
              ...(recommendedHashtags.length > 0
                ? { recommendedHashtags }
                : {}),
              options: params.options
                .filter((option) => !!option.label)
                .map((option, i) => {
                  return {
                    description: option.label,
                    responses: [],
                    order: i + 1,
                  };
                }),

              ...(props.organization
                ? params.isSponsoredPost
                  ? {
                    sponsorData: {
                      isSponsoredPost: true,
                      audience: { groupId: params.group },
                    },
                    organizationId: props.organization._id,
                  }
                  : { organizationId: props.organization._id }
                : { groupId: params.group }),
            },
          },
        },
        update(cache, { data }) {
          if (!data?.createPollV2.data) {
            return;
          }
          updateCacheOnPostCreation({
            cache,
            post: data.createPollV2.data,
          });
        },
      });
      const createdPoll = result.data?.createPollV2.data;
      if (!createdPoll) {
        return;
      }
      const optionLabels = params.options
        .map((option) => option.label.trim())
        .filter(Boolean);
      const bodyText = params.body.getCurrentContent().getPlainText();
      const pollText = [
        params.title,
        bodyText,
        ...optionLabels,
      ].filter(Boolean).join('\n');
      const contentAnalysis = analyzeContent(pollText);

      post_created({
        entity_type: 'post',
        entity_id: createdPoll._id,
        post_type: 'poll',
        content_type: 'poll',
        content_has_tags: !!params.tags && params.tags.length > 0,
        content_has_media: false,
        post_is_announcement: params.isAnnouncement,
        post_is_scheduled: !!params.scheduledPublishDate,
        location_posted_in: props.organization
          ? 'organization'
          : createdPoll.group?.isGlobalSingleton
            ? 'home'
            : 'group',
        location_group_id: !props.organization ? params.group : undefined,
        location_organization_id: props.organization?._id,
        content_text: contentAnalysis.contentText,
        content_length: contentAnalysis.contentLength,
        content_word_count: contentAnalysis.wordCount,
        content_has_question_mark: contentAnalysis.hasQuestionMark,
        content_has_links: contentAnalysis.hasLinks,
        content_link_count: contentAnalysis.linkCount,
        content_mention_count: contentAnalysis.mentionCount,
        content_hashtag_count: contentAnalysis.hashtagCount,
        content_hashtags: contentAnalysis.hashtags,
        poll_id: createdPoll._id,
        poll_option_count: optionLabels.length,
        poll_options_text: optionLabels,
      });
      props.onClose();

      params.scheduledPublishDate &&
        toast({
          description: `Poll scheduled for ${dayjs(
            params.scheduledPublishDate
          ).format('MMM D, h:mm A')}`,
          status: 'success',
        });
    } catch (err) {
      toast({
        status: 'error',
        description: 'Failed to create poll.',
      });
      logError(err);
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = () => {
    /**
     * If the form is invalid, reset `scheduledPublishDate`
     * User should be able to click `Post` to create the post
     * immediately after the form fields become valid
     */
    methods.resetField('scheduledPublishDate');
  };

  return (
    <FormProvider {...methods}>
      <AnnouncePostModal
        open={isAnnounceModalOpen}
        onClose={() => setIsAnnounceModalOpen(false)}
        onClickAnnounce={() => {
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        onClickPost={() => {
          methods.setValue('isAnnouncement', false);
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        announcementLoading={false}
        loading={false}
      />

      <ModalBody px="6" py="4">
        <PollForm
          organizationId={props.organization?._id}
          initialGroupId={props.group?._id}
        />
      </ModalBody>

      <PostingModalFooter>
        <Flex w="100%" align="center">
          <Button
            variant="ghost"
            colorScheme="gray"
            onClick={props.onClose}
            display={{ base: 'inline-flex', md: 'inline-flex', lg: 'none' }}
            mr={2}
            border="1px solid"
            borderColor="gray.300"
          >
            Cancel
          </Button>
          <Spacer />
          {canSchedulePosts && (
            <Controller
              name="scheduledPublishDate"
              render={({ field: { onBlur, onChange, ref, value, name } }) => (
                <Box mr={2}>
                  <SchedulePostMenu
                    value={value}
                    onChange={(value) => {
                      onChange(value);
                    }}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={
                      (formState.isSubmitted && !formState.isValid) ||
                      loading
                    }
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                  />
                </Box>
              )}
            />
          )}
          <Button
            isLoading={loading}
            isDisabled={formState.isSubmitted && !formState.isValid}
            onClick={
              isAnnouncement
                ? () => setIsAnnounceModalOpen(true)
                : handleSubmit(onSubmit, onInvalid)
            }
          >
            {scheduledPublishDate ? 'Schedule Post' : 'Post Now'}
          </Button>
        </Flex>
      </PostingModalFooter>
    </FormProvider>
  );
};
